import * as React from 'react'
import { ProcessedMessage } from './useProcessMessage'
import useUser from '../useUser'
import { LocalizedValueSetting, ModuleType, TimeSuggestionDescriptionOptionEnum, TimeSuggestionOptionEnum } from '../../context/GlobalContext'
import useModules from '../module/useModules'
import useRoles, { rolesWriteAllTimes } from '../useRoles'
import TimeSuggestionToastContent from '../../components/Toast/TimeSuggestionToastContent'
import useQueueStateHandler, { QueueStatus } from '../useQueueStateHandler'
import { AxiosInstance, AxiosResponse } from 'axios'
import { useSettings } from '../useSettings'
import { History } from 'history'
import { COMMAND_PREFIX } from '../../pages/Commands'
import { getOverviewItemDescription, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../pages/Time/BaseTimeSuggestionOverview'
import { TrackTimeSuggestionDto } from '../../components/Dialog/TimeSuggestion/TimeSuggestionDialog'
import { Command, IBackgroundLocationState } from '../../config/appConfig'
import { formatDifferenceInTime, parseDateTime } from '../../helpers/dateHelper'
import { useTranslation } from 'react-i18next'
import useAxios from '../useAxios'
import { useHistory } from 'react-router-dom'

/**
 * Messages originating from the Inboxer in order to invoke a TimeSuggestionDialog.
 */
export type TimeTrackingNotification = ProcessedMessage<{
    fileNumber: string
    timeSuggestionId: string
    date: string
    duration: string
    sender: string
    recipients: string
    emailInbound: boolean
    subject: string
    isAutomatic: boolean
    isBatch: boolean
    references: string[]
}>

function showTimeSuggestionDialog(
    message: TimeTrackingNotification,
    axios: AxiosInstance,
    history: History<IBackgroundLocationState>,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
) {
    if (message.data?.timeSuggestionId === undefined) {
        return
    }

    axios
        .get<TimeSuggestionOverviewDto>(`/time-suggestion/${message.data.timeSuggestionId}`, {
            headers: { SuppressErrors: 'true', 'If-None-Match': null },
        })
        .then(response => {
            const timeSuggestion = response.data as TimeSuggestionOverviewDto
            // If we already tracked time for this email, skip popup.
            if (timeSuggestion.status !== TimeSuggestionStatus.ToReview) {
                return
            }

            const timeMutation: Partial<TrackTimeSuggestionDto> = {
                timeSuggestionId: message.data.timeSuggestionId,
                fileId: timeSuggestion.file.id,
                date: parseDateTime(timeSuggestion.emailSent).toFormat('yyyy-LL-dd'),
                duration: formatDifferenceInTime(timeSuggestion.emailCreated, timeSuggestion.emailSent, '00:01'),
                description: getOverviewItemDescription(timeSuggestion, language, setting),
                emailInbound: timeSuggestion.emailInbound,
            }
            history.push(`${COMMAND_PREFIX}/${Command.TrackTime}`, {
                background: history?.location?.state?.background ?? history?.location,
                data: timeMutation,
            })
        })
}

/**
 * Process a time-tracking event message. This invokes the TimeSuggestionDialog with custom initialValues.
 */
export default function useProcessTimeTrackingNotification() {
    const axios = useAxios()
    const history = useHistory<IBackgroundLocationState>()
    const modules = useModules()
    const roles = useRoles()
    const { settings } = useSettings()
    const { i18n } = useTranslation()
    const { currentUser } = useUser()

    const handler = useQueueStateHandler(props => <TimeSuggestionToastContent {...props} />)

    return (message: TimeTrackingNotification) => {
        if (!modules.has(ModuleType.TimeTracking) || !roles.any(rolesWriteAllTimes) || currentUser?.useTimePopup === false) {
            // Only push a time tracking popup to time tracking users that want a popup.
            return
        }

        if (message.data?.timeSuggestionId === undefined) {
            return
        }

        let notificationOption: TimeSuggestionOptionEnum
        if (message.data?.isAutomatic) {
            notificationOption = message.data?.emailInbound ? settings.timeSuggestion?.incomingEmail : settings.timeSuggestion?.sentEmail
        } else {
            notificationOption = message.data?.isBatch ? settings.timeSuggestion?.fileMultipleEmail : settings.timeSuggestion?.fileSingleEmail
        }
        if (notificationOption === undefined) {
            notificationOption = TimeSuggestionOptionEnum.Notification
        }

        if (notificationOption === TimeSuggestionOptionEnum.Popup) {
            showTimeSuggestionDialog(message, axios, history, i18n.language, settings.timeSuggestionDescription)
        } else if (notificationOption === TimeSuggestionOptionEnum.Notification) {
            handler.dispatch(message.data.timeSuggestionId, {
                baseURL: undefined,
                url: `/time-suggestion/${message.data.timeSuggestionId}`,
                method: 'GET',
                mapDispatchResponse: (response: AxiosResponse<void>) => response.data,
                callbacks: {
                    [QueueStatus.Queued]: data => {
                        handler.transition(message.data.timeSuggestionId, QueueStatus.Finished, { ...data.status, current: QueueStatus.Finished })
                    },
                },
            })
        } else {
            console.log('Notification option for time tracking of email is NONE.')
        }
    }
}
